import React from 'react';
import { FaExternalLinkAlt, FaPlay } from 'react-icons/fa';
import { AltNavBar } from '../components/NavBar';
import Localization from '../localization';
import { formatDate } from '../utils/utils';
import ReactGA from 'react-ga4';

import conf from '../config/web-site.json';

import '../styles/Press.scss';

const strings = Localization.getStrings();

type PressProps = {
  scroller: (arg: string) => void;
  config?: typeof conf;
}

const isVideoSource = (url: string): boolean =>
  url.includes('youtu') || url.includes('vimeo.com') || url.includes('fox13');

const getSourceLabel = (url: string): string => {
  if (url.includes('youtu')) return 'YouTube';
  if (url.includes('vimeo.com')) return 'Vimeo';
  if (url.includes('fox13')) return 'TV News';
  if (url.includes('969theeagle')) return 'Radio';
  return 'Article';
};

const Press: React.FC<PressProps> = (props) => {
  const { scroller, config = conf } = props;
  const pressKeys: (keyof typeof config.press)[] = Object.keys(config.press) as (keyof typeof config.press)[];
  const [featured, ...rest] = pressKeys;

  return (
    <section id="press" className="press-section" data-testid="press-view">
      <AltNavBar {...{ scroller, source: config.pages.press.title }} />
      <h1 className="press-heading">{strings['b_press']}</h1>

      {/* ── Featured hero article ── */}
      <div className="press-hero">
        <div className="press-hero-image-wrap">
          <img
            src={require(`../assets/img/press/${config.press[featured].image}`)}
            alt={strings[`${featured}_title`]}
            className="press-hero-img"
          />
          {isVideoSource(config.press[featured].url) && (
            <div className="press-play-badge" aria-hidden="true">
              <FaPlay />
            </div>
          )}
          <div className="press-hero-overlay">
            <span className="press-source-badge">{getSourceLabel(config.press[featured].url)}</span>
            <strong className="press-hero-title">{strings[`${featured}_title`]}</strong>
            <p className="press-hero-desc">{strings[`${featured}_description`]}</p>
            <span className="press-hero-date">{formatDate(config.press[featured].date)}</span>
            <a
              href={config.press[featured].url}
              target="_blank"
              rel="noopener noreferrer"
              className="press-watch-btn"
              data-testid="press-featured-link"
              onClick={(event) => {
                ReactGA.event({
                  category: config.analytics.parameters.categories.navigation,
                  action: `${event.currentTarget.title} Navigated to ${strings[`${featured}_title`]}`,
                  label: config.analytics.parameters.labels.click,
                });
              }}
            >
              <span>{strings.b_watch_here}</span>
              <FaExternalLinkAlt style={{ verticalAlign: 'text-top' }} />
            </a>
          </div>
        </div>
      </div>

      {/* ── Press grid ── */}
      <div className="press-grid" role="list">
        {rest.map((key) => (
          <a
            key={key}
            href={config.press[key].url}
            target="_blank"
            rel="noopener noreferrer"
            className="press-card"
            role="listitem"
            data-testid="press-card"
            onClick={() => {
              ReactGA.event({
                category: config.analytics.parameters.categories.navigation,
                action: `Navigated to ${strings[`${key}_title`]} article`,
                label: config.analytics.parameters.labels.click,
              });
            }}
          >
            <div className="press-card-image-wrap">
              <img
                src={require(`../assets/img/press/${config.press[key].image}`)}
                alt={strings[`${key}_title`]}
                className="press-card-img"
              />
              {isVideoSource(config.press[key].url) && (
                <div className="press-play-badge" aria-hidden="true">
                  <FaPlay />
                </div>
              )}
              <div className="press-card-overlay">
                <span className="press-source-badge">{getSourceLabel(config.press[key].url)}</span>
                <strong className="press-card-title">{strings[`${key}_title`]}</strong>
                <p className="press-card-desc">{strings[`${key}_description`]}</p>
                <span className="press-card-date">{formatDate(config.press[key].date)}</span>
              </div>
            </div>
          </a>
        ))}
      </div>
    </section>
  );
};

export default Press;